/**
 * The trigger set that decides when the probe re-runs. Hit-testing is
 * pull-based — there is no occlusion event — so correctness rests on these
 * triggers firing whenever the answer changes. Measured live against a
 * per-frame probe: zero divergences, 0ms lag.
 */

import { BOTTOM_EDGE_ATTR, BOTTOM_EDGE_SELECTOR } from "@/lib/bottom-edge/probe"

export type TriggerHarness = {
  /** Set when a trigger has fired and the tracked value is due for a refresh. */
  consumePending: () => boolean
  /** True while an animation/transition is in flight — probe every frame. */
  isHot: (now: number) => boolean
  /** The marked bars currently in the document, kept from mutation records. */
  markers: () => ReadonlySet<Element>
  stop: () => void
}

/**
 * How long after a transition/animation starts we keep probing every frame.
 * Capped rather than paired with `transitionend`, which a cancelled or
 * interrupted transition can drop.
 */
const HOT_WINDOW_MS = 700

export function createTriggerHarness(): TriggerHarness {
  let pending = true
  let hotUntil = 0

  const fire = (hot = false) => {
    pending = true
    if (hot) hotUntil = performance.now() + HOT_WINDOW_MS
  }

  const onScroll = () => fire()
  const onResize = () => fire()
  const onViewport = () => fire()

  /**
   * Only animations that can change the answer — unfiltered, an idle chat
   * page fired 117 animation events (shimmers, spinners), each opening a hot
   * window. This cannot drop a real signal: a bar that moves is marked or
   * contains a marker, a bar that *appears* arrives as a childList mutation,
   * and so does a sheet sliding up over an unmarked area.
   */
  const affectsBottomEdge = (target: EventTarget | null) => {
    if (!(target instanceof Element)) return false
    for (const marker of markers) {
      if (marker.contains(target) || target.contains(marker)) return true
    }
    return false
  }

  // Only a *start* opens the hot window; end and cancel still set pending so
  // the settled position gets its one refresh.
  const onTransition = (e: Event) => {
    if (affectsBottomEdge(e.target)) {
      fire(e.type === "transitionrun")
    }
  }
  const onAnimation = (e: Event) => {
    if (affectsBottomEdge(e.target)) {
      fire(e.type === "animationstart")
    }
  }

  // Capture phase: transitions and animations on descendants don't bubble
  // reliably for our purposes, and sheets animate deep in a portal.
  window.addEventListener("scroll", onScroll, { capture: true, passive: true })
  window.addEventListener("resize", onResize, { passive: true })
  document.addEventListener("transitionrun", onTransition, true)
  document.addEventListener("transitionend", onTransition, true)
  document.addEventListener("transitioncancel", onTransition, true)
  document.addEventListener("animationstart", onAnimation, true)
  document.addEventListener("animationend", onAnimation, true)
  document.addEventListener("animationcancel", onAnimation, true)
  window.visualViewport?.addEventListener("resize", onViewport)
  window.visualViewport?.addEventListener("scroll", onViewport)

  // The marker set is kept from mutation records rather than re-queried: a
  // document-wide attribute query on every element-touching batch cost more
  // than the probe itself on a 6k-element collection page where 46 of 47
  // batches touched no marker. Added subtrees are scanned in place, removed
  // markers are pruned by connectedness, and the attribute filter delivers
  // marks that flip in place. A ResizeObserver rides the same set: a marked
  // bar can change size with no CSS event at all (the chat composer autosize
  // writes `style.height` directly).
  const markers = new Set<Element>()
  const markerResize = new ResizeObserver(() => fire())
  const track = (el: Element) => {
    if (markers.has(el)) return
    markers.add(el)
    markerResize.observe(el)
  }
  const release = (el: Element) => {
    if (!markers.delete(el)) return
    markerResize.unobserve(el)
  }
  for (const el of document.querySelectorAll(BOTTOM_EDGE_SELECTOR)) track(el)

  const applyMutations = (records: MutationRecord[]) => {
    for (const record of records) {
      if (record.type === "attributes") {
        const el = record.target as Element
        if (el.matches(BOTTOM_EDGE_SELECTOR)) track(el)
        else release(el)
        continue
      }
      for (const node of record.addedNodes) {
        if (node.nodeType !== Node.ELEMENT_NODE) continue
        const el = node as Element
        if (el.matches(BOTTOM_EDGE_SELECTOR)) track(el)
        for (const inner of el.querySelectorAll(BOTTOM_EDGE_SELECTOR)) {
          track(inner)
        }
      }
    }
    for (const el of markers) if (!el.isConnected) release(el)
  }

  // Portals mount at <body>, so a subtree observer is how sheets and bars are
  // seen appearing. Every batch sets pending — pure text can move an
  // occlusion boundary — and only the records say whether the set changed.
  const observer = new MutationObserver((records) => {
    fire()
    applyMutations(records)
  })
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    // The marker only: `class`/`style` on every node is a very wide net on a
    // streaming page, and a class swap that actually *moves* a bar
    // transitions, which the transition trigger catches.
    attributeFilter: [BOTTOM_EDGE_ATTR],
  })

  return {
    consumePending: () => {
      if (!pending) return false
      pending = false
      return true
    },
    isHot: (now: number) => now < hotUntil,
    markers: () => markers,
    stop: () => {
      window.removeEventListener("scroll", onScroll, { capture: true })
      window.removeEventListener("resize", onResize)
      document.removeEventListener("transitionrun", onTransition, true)
      document.removeEventListener("transitionend", onTransition, true)
      document.removeEventListener("transitioncancel", onTransition, true)
      document.removeEventListener("animationstart", onAnimation, true)
      document.removeEventListener("animationend", onAnimation, true)
      document.removeEventListener("animationcancel", onAnimation, true)
      window.visualViewport?.removeEventListener("resize", onViewport)
      window.visualViewport?.removeEventListener("scroll", onViewport)
      observer.disconnect()
      markerResize.disconnect()
    },
  }
}
